/**
 * AiElectronicModuleEnrichPanel — the module-specific AI enrichment UI.
 *
 * The `enrichElectronicModule` callable returns a per-field result map
 * (`{ displayType: { value, confidence, reasoning }, … }`) — a genuinely
 * different shape from the calibre enrichment result, so this panel is a
 * lightweight mirror of AiCalibreEnrichPanel rather than an adapter over it.
 *
 * Presentation only: an optional context textarea, a manual Run trigger (gated
 * on a saved `moduleId`), loading/error states, and a read-only per-field
 * preview of what the model proposed. The caller owns the run wiring and the
 * apply-review step (which reuses the editor's existing diff modal).
 */
import type { ModuleEnrichResult } from '../../services/firebase';
import { getModuleFieldLabel } from '../../services/moduleEnrichFields';
import { Button } from '../common/Button';

/** Async state for the module AI enrichment flow. */
export interface ModuleAiEnrichState {
  loading?: boolean;
  result?: ModuleEnrichResult;
  error?: string;
}

/**
 * Friendly label for the per-field preview.
 *
 * Reads `moduleEnrichFields` rather than keeping a second list beside it: this
 * panel, the bulk review grid and the editor's apply-diff all name the same
 * fields, and the copy that used to live here drifted from the registry the
 * moment either gained a field. `description` is the older spelling of
 * `aiDescription` and resolves to the same label; anything the registry doesn't
 * know falls back to the key.
 */
const fieldLabel = (key: string): string =>
  getModuleFieldLabel(key === 'description' ? 'aiDescription' : key);

function renderValue(value: unknown): string {
  if (Array.isArray(value))
    return value.filter((v) => v != null && v !== '').join(', ');
  if (value == null) return '';
  return String(value);
}

const confidenceClass = (c?: string): string => {
  switch (c) {
    case 'high':
      return 'bg-emerald-100 text-emerald-800';
    case 'medium':
      return 'bg-amber-100 text-amber-800';
    case 'low':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-100 text-gray-600';
  }
};

interface AiElectronicModuleEnrichPanelProps {
  state: ModuleAiEnrichState;
  /** Optional free-text hint passed to the callable (e.g. a reference name). */
  context: string;
  onContextChange: (value: string) => void;
  /** True when the module has not been saved yet (no `moduleId`). */
  runDisabled: boolean;
  /** Tooltip explaining why Run is disabled (shown when `runDisabled`). */
  disabledReason?: string;
  onRun: () => void;
  /** Open the apply-review diff. */
  onAccept: () => void;
  onClose: () => void;
}

export function AiElectronicModuleEnrichPanel({
  state,
  context,
  onContextChange,
  runDisabled,
  disabledReason,
  onRun,
  onAccept,
  onClose,
}: AiElectronicModuleEnrichPanelProps) {
  const { loading, result, error } = state;
  const entries = result
    ? Object.entries(result).filter(
        ([, r]) => r && renderValue(r.value).trim().length > 0,
      )
    : [];

  return (
    <div className="space-y-3">
      <div>
        <label className="block text-xs font-medium text-purple-700 mb-1">
          Context (optional)
        </label>
        <textarea
          value={context}
          onChange={(e) => onContextChange(e.target.value)}
          rows={2}
          placeholder="Any grounding hint for the model — e.g. the reference this module powers…"
          className="w-full border border-purple-200 bg-white rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-400 focus:border-purple-400"
        />
      </div>

      <div className="flex items-center gap-2">
        <Button
          variant="secondary"
          size="sm"
          onClick={onRun}
          disabled={runDisabled || loading}
          title={runDisabled ? disabledReason : undefined}
        >
          {loading ? 'Enriching…' : result ? 'Re-run enrichment' : 'Run enrichment'}
        </Button>
        <Button variant="ghost" size="sm" onClick={onClose}>
          Close
        </Button>
        {runDisabled && disabledReason && (
          <span className="text-xs text-amber-700">{disabledReason}</span>
        )}
      </div>

      {error && (
        <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
          {error}
        </div>
      )}

      {result && !loading && (
        <div className="space-y-2">
          {entries.length === 0 ? (
            <div className="text-sm text-gray-500">
              No suggestions returned for this module.
            </div>
          ) : (
            <div className="border border-purple-200 rounded-lg overflow-hidden max-h-72 overflow-y-auto bg-white">
              <table className="w-full text-sm">
                <tbody className="divide-y divide-gray-100">
                  {entries.map(([key, r]) => (
                    <tr key={key} className="align-top">
                      <td className="px-3 py-2 text-gray-600 whitespace-nowrap">
                        {fieldLabel(key)}
                      </td>
                      <td className="px-3 py-2 text-gray-900 break-words">
                        <div className="font-mono text-emerald-700">
                          {renderValue(r.value)}
                        </div>
                        {r.reasoning && (
                          <div className="text-[11px] text-gray-400 mt-0.5">
                            {r.reasoning}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {r.confidence && (
                          <span
                            className={`px-2 py-0.5 rounded text-[10px] font-medium ${confidenceClass(
                              r.confidence,
                            )}`}
                          >
                            {r.confidence}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {entries.length > 0 && (
            <div className="flex justify-end">
              <Button variant="primary" size="sm" onClick={onAccept}>
                Review &amp; apply…
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
